import { useEffect } from "react";

export type CuePoint = {
  time: number;
  params?: Record<string, any>;
  id?: string;
};

export type OnCue = (
  cue: CuePoint,
  ctx: { currentTime: number }
) => void | undefined;

const getCueClass = () =>
  (window as any).VTTCue || (window as any).TextTrackCue;

export function useVideoCuePoints(
  videoRef: React.RefObject<HTMLVideoElement>,
  cuePoints: CuePoint[] | undefined,
  handleCue: OnCue
) {
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !cuePoints?.length) return;

    const Cue = getCueClass();
    if (!Cue) return;

    const track = videoEl.addTextTrack("metadata", "app-cuepoints", "en");
    track.mode = "hidden";

    const list = cuePoints
      .slice()
      .filter((c) => Number.isFinite(c.time))
      .sort((a, b) => a.time - b.time)
      .map((c, i) => ({
        ...c,
        id: c.id ?? `cue-${i}-${Math.round(c.time * 1000)}`,
      }));

    const cueById = new Map<string, CuePoint>();
    for (const cp of list) {
      const start = Math.max(0, cp.time);
      const end = start + 0.5; // duración fija
      const payload = JSON.stringify(cp.params ?? {});
      const cue = new Cue(start, end, payload);
      (cue as any).id = cp.id!;
      track.addCue(cue);
      cueById.set(cp.id!, cp);
    }

    track.addEventListener("cuechange", () => {
      const active = Array.from(track.activeCues ?? []) as any[];
      for (const cue of active) {
        const id: string =
          (cue.id as string) ||
          `${cue.startTime}-${cue.endTime}-${String(cue.text).slice(0, 24)}`;
        const base = cueById.get(id);
        let params: any = {};
        try {
          params = JSON.parse(String(cue.text || "{}"));
        } catch {
          /* ignore malformed cue payloads */
        }
        const cp: CuePoint = base
          ? base
          : { id, time: cue.startTime, params: params ?? {} };
        handleCue(cp, { currentTime: videoEl.currentTime });
      }
    });

    return () => {
      const cues = Array.from(track.cues ?? []);
      for (const c of cues) track.removeCue(c);
    };
  }, [videoRef, cuePoints, handleCue]);
}
